import React, {useEffect, useState} from "react";
import {
    Table,
    Card,
    Button,
    Modal,
    Form,
    Input,
    DatePicker,
    message,
    Popconfirm,
    Space,
    Tag,
    Divider,
    Row,
    Col,
} from "antd";
import dayjs from "dayjs";
import api from "../../api";
import {PlusOutlined} from "@ant-design/icons";

const AllUsersPage: React.FC = () => {
    const [users, setUsers] = useState<any[]>([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState<any>(null);
    const [form] = Form.useForm();
    const [totalUsers, setTotalUsers] = useState(0);
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    useEffect(() => {
        fetchUsers(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize]);

    useEffect(() => {
        checkAdmin();
    }, []);

    const fetchUsers = async (page = 1, pageSize = 10) => {
        try {
            const res = await api.get("/users/", {
                params: {
                    page,
                    page_size: pageSize,
                },
            });

            const usersArray = Array.isArray(res.data) ? res.data : res.data.results || [];
            const nonSuperusers = usersArray.filter((user: any) => !user.is_superuser);

            setUsers(nonSuperusers);
            setTotalUsers(res.data.count || 0);
        } catch (err) {
            console.error("Failed to fetch users", err);
            message.error("Failed to fetch users");
        }
    };

    const checkAdmin = async () => {
        const res = await api.get("/users/me/");
        const user = res.data;
        setIsAdmin(user.is_staff || user.is_superuser);
        setCurrentUserId(user.id);
    };

    const handleAddOrEditUser = async (values: any) => {
        const payload = {
            ...values,
            birthday: values.birthday
                ? dayjs(values.birthday).format("YYYY-MM-DD")
                : null,
        };
        try {
            if (editingUser) {
                await api.patch(`/users/${editingUser.id}/`, payload);
                message.success("User updated");
            } else {
                await api.post("/users/", payload);
                message.success("User created");
            }
            setModalVisible(false);
            form.resetFields();
            setEditingUser(null);
            fetchUsers(pagination.current, pagination.pageSize);
        } catch (err) {
            message.error("Failed to save user");
        }
    };

    const handleDeleteUser = async (id: number) => {
        try {
            await api.delete(`/users/${id}/`);
            message.success("User deleted");
            fetchUsers(pagination.current, pagination.pageSize);
        } catch {
            message.error("Failed to delete user");
        }
    };

    const columns = [
        {title: "First Name", dataIndex: "first_name"},
        {title: "Last Name", dataIndex: "last_name"},
        {title: "Phone", dataIndex: "phone"},
        {title: "Birthday", dataIndex: "birthday"},
        {
            title: "Role",
            render: (_: any, record: any) => {
                if (record.username === "inspire-vip") {
                    return <Tag color="red">API</Tag>;
                }

                return record.is_superuser || record.is_staff ? (
                    <Tag color="geekblue">Admin</Tag>
                ) : (
                    <Tag>User</Tag>
                );
            },
        },
        isAdmin && {
            title: "Actions",
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        size="small"
                        onClick={() => {
                            setEditingUser(record);
                            form.setFieldsValue({
                                ...record,
                                birthday: record.birthday ? dayjs(record.birthday) : null,
                            });
                            setModalVisible(true);
                        }}
                    >
                        Edit
                    </Button>
                    {record.id !== currentUserId && record.username !== "inspire-vip" && (
                        <Popconfirm
                            title="Are you sure?"
                            onConfirm={() => handleDeleteUser(record.id)}
                        >
                            <Button danger size="small">
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ].filter(Boolean);

    return (
        <Card
            title={<h2 className="text-lg font-semibold">All Users</h2>}
            className="rounded-2xl shadow-sm"
            extra={
                isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={() => {
                            setEditingUser(null);
                            form.resetFields();
                            setModalVisible(true);
                        }}
                        className="rounded-lg"
                    >
                        Add User
                    </Button>
                )
            }
        >
            <Table
                dataSource={users}
                rowKey="id"
                columns={columns as any}
                scroll={{x: true}}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: totalUsers,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                className="rounded-xl overflow-hidden"
            />

            <Modal
                open={modalVisible}
                title={
                    <span className="text-base font-semibold">
                        {editingUser ? "Edit User" : "Create New User"}
                    </span>
                }
                onCancel={() => {
                    setModalVisible(false);
                    setEditingUser(null);
                }}
                onOk={() => form.submit()}
                okText={editingUser ? "Update" : "Create"}
                okButtonProps={{className: "rounded-md"}}
                cancelButtonProps={{className: "rounded-md"}}
                centered
                width={600}
            >
                <Divider className="my-2"/>
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleAddOrEditUser}
                    scrollToFirstError
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="username"
                                label="Username"
                                rules={[
                                    {required: true, message: "Username is required"},
                                    {
                                        pattern: /^\S+$/,
                                        message: "Username cannot contain spaces",
                                    },
                                ]}
                            >
                                <Input placeholder="Enter username"/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[{required: true, type: "email", message: "Please enter a valid email"}]}
                            >
                                <Input placeholder="Enter email address"/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="first_name"
                                label="First Name"
                                rules={[{required: true, message: "Please enter a first name"}]}
                            >
                                <Input placeholder="Enter first name"/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="last_name"
                                label="Last Name"
                                rules={[{required: true, message: "Please enter a last name"}]}
                            >
                                <Input placeholder="Enter last name"/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label="Phone Number"
                                rules={[{required: true, message: "Please enter a phone number"}]}>
                                <Input placeholder="Enter phone number"/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="birthday" label="Birthday">
                                <DatePicker style={{width: "100%"}}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    {!editingUser && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="password"
                                        label="Password"
                                        rules={[{required: true, message: "Please enter a password"}]}
                                    >
                                        <Input.Password placeholder="Enter password"/>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="confirm"
                                        label="Confirm Password"
                                        dependencies={["password"]}
                                        rules={[
                                            {required: true},
                                            ({getFieldValue}) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue("password") === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(
                                                        new Error("Passwords do not match")
                                                    );
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password placeholder="Confirm password"/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form>
            </Modal>
        </Card>
    );
};

export default AllUsersPage;
